"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { startPageProgress, finishPageProgress, subscribePageProgress } from "@/lib/progress";

function isInternalNav(anchor: HTMLAnchorElement, event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return false;
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return false;
  return true;
}

export function TopProgress() {
  const pathname = usePathname();
  const firstPath = useRef(true);
  const hideTimer = useRef<number>(0);
  const shown = useRef(false);
  const [visible, setVisible] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    return subscribePageProgress((busy) => {
      window.clearTimeout(hideTimer.current);
      if (busy) {
        shown.current = true;
        setFinishing(false);
        setVisible(true);
        return;
      }
      if (!shown.current) return;
      setFinishing(true);
      hideTimer.current = window.setTimeout(() => {
        shown.current = false;
        setVisible(false);
        setFinishing(false);
      }, 280);
    });
  }, []);

  useEffect(() => {
    if (!visible || finishing) return;
    const id = window.setTimeout(() => finishPageProgress(), 12000);
    return () => window.clearTimeout(id);
  }, [visible, finishing]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.("a");
      if (!anchor || !isInternalNav(anchor, event)) return;
      startPageProgress();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    finishPageProgress();
  }, [pathname]);

  if (!visible) return null;

  return (
    <div className="top-progress" aria-hidden>
      <div className={finishing ? "top-progress-bar is-done" : "top-progress-bar is-running"} />
    </div>
  );
}
